import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const emptyForm = {
  name: 'Main warehouse',
  companyName: '',
  contactName: '',
  phone: '',
  email: '',
  street1: '',
  street2: '',
  city: '',
  stateOrProvince: '',
  postalCode: '',
  countryCode: 'US',
  isDefault: true,
  pickupReadyTime: '14:00',
  pickupCloseTime: '17:00',
};

export default function ShipFromSettingsPage() {
  const location = useLocation();
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const canEdit =
    auth.type === 'ADMIN' || (auth.permissions || []).includes('CARRIER_ACCOUNT:UPDATE');
  const [form, setForm] = useState(emptyForm);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authorizedFetch(`${API_BASE}/carriers/ship-from`, {
        headers: { 'x-department-slug': '' },
      });
      if (!res.ok) throw new Error('load');
      const rows = (await res.json()) as Array<{ id: string; isDefault: boolean } & typeof emptyForm>;
      const def = rows.find((r) => r.isDefault) || rows[0];
      if (def) {
        setLocationId(def.id);
        setForm({
          name: def.name,
          companyName: def.companyName,
          contactName: def.contactName,
          phone: def.phone,
          email: def.email || '',
          street1: def.street1,
          street2: def.street2 || '',
          city: def.city,
          stateOrProvince: def.stateOrProvince,
          postalCode: def.postalCode,
          countryCode: def.countryCode,
          isDefault: def.isDefault,
          pickupReadyTime: def.pickupReadyTime || '14:00',
          pickupCloseTime: def.pickupCloseTime || '17:00',
        });
      }
    } catch {
      setError('Failed to load ship-from location');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const url = locationId
        ? `${API_BASE}/carriers/ship-from/${locationId}`
        : `${API_BASE}/carriers/ship-from`;
      const res = await authorizedFetch(url, {
        method: locationId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'x-department-slug': '' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('save');
      await load();
    } catch {
      setError('Failed to save ship-from location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath={location.pathname}>
      <main className="workspace-page">
        <header className="workspace-page-header">
          <div>
            <h1 className="workspace-page-title">Ship-from location</h1>
            <p className="workspace-page-subtitle text-slate-600">
              Warehouse address used for FedEx and UPS labels and pickups.
            </p>
          </div>
        </header>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {loading ? (
          <p className="text-sm text-slate-600">{t('common.loading')}</p>
        ) : (
          <section className="workspace-panel grid gap-3 md:grid-cols-2">
            {(['name', 'companyName', 'contactName', 'phone', 'email', 'street1', 'street2', 'city', 'stateOrProvince', 'postalCode', 'countryCode'] as const).map((key) => (
              <label key={key} className="flex flex-col gap-1 text-sm">
                {key}
                <input
                  className="border border-slate-300 px-2 py-2"
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
              </label>
            ))}
            <label className="flex flex-col gap-1 text-sm">
              Pickup ready (HH:mm)
              <input className="border border-slate-300 px-2 py-2" value={form.pickupReadyTime} onChange={(e) => setForm((f) => ({ ...f, pickupReadyTime: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Pickup close (HH:mm)
              <input className="border border-slate-300 px-2 py-2" value={form.pickupCloseTime} onChange={(e) => setForm((f) => ({ ...f, pickupCloseTime: e.target.value }))} />
            </label>
            {canEdit ? (
              <div className="md:col-span-2">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => void save()}
                  className="border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-medium text-white"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            ) : null}
          </section>
        )}
      </main>
    </MainLayout>
  );
}
